import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { uniqBy } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

import { Drawer, DrawerProps } from '@/components/Layout/Drawer';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectMinorExtraQuestions } from '@/redux/configs/selectors';
import { notifyMinorDataChanged, selectOperationData } from '@/redux/operation';
import { selectPEPQuestions } from '@/redux/user-data/selectors';

import { PEPQuestion, QuestionCode, QuestionWithAnswer } from '@/types/api/pep';
import { User } from '@/types/user';

import { MinorQuestionConsentSign } from './MinorQuestionConsentSign/MinorQuestionConsentSign';
import { MinorQuestionOther } from './MinorQuestionOther';
import { QuestionAboutDualCitizenship } from './QuestionAboutDualCitizenship';
import { QuestionAboutRelatives } from './QuestionAboutRelatives';
import { QuestionAboutUSIndicia } from './QuestionAboutUSIndicia';
import { QuestionAccountTypeOrder } from './QuestionAccountTypeOrder';
import { QuestionProps } from './QuestionDrawer.interface';
import { QuestionEXCPrimaryAccount } from './QuestionEXCPrimaryAccount';
import { QuestionRenouncedUS } from './QuestionRenouncedUS';

import { QuestionsContainer } from './QuestionDrawer.styled';

interface QuestionsDrawerProps extends DrawerProps {
  user: User;
}

export function MinorQuestionsDrawer({
  user: minorUser,
  ...props
}: Readonly<QuestionsDrawerProps>) {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const rawQuestions = useAppSelector(selectPEPQuestions);
  const extraQuestions = useAppSelector(selectMinorExtraQuestions);
  const { shouldRefreshMinorData } = useAppSelector(selectOperationData);
  const [questionFlow, setQuestionFlow] = useState<{
    questionList: QuestionWithAnswer[];
    currentIndex: number;
  }>({
    questionList: [],
    currentIndex: 0,
  });

  const [pendingClose, setPendingClose] = useState(false);

  useEffect(() => {
    if (rawQuestions && props.open) {
      const newQuestions: PEPQuestion[] = [];
      const sortedRawQuestions = [...rawQuestions]
        .filter(
          q =>
            q.code !== QuestionCode.Personal &&
            q.code !== QuestionCode.Connection,
        )
        .sort((a, b) => a.priority - b.priority);

      sortedRawQuestions.forEach(question => {
        if (!(minorUser?.pepAnswered || []).includes(question.code)) {
          newQuestions.push(question);
        }
      });

      newQuestions.push(
        ...extraQuestions.map(item => ({
          ...item,
          question: t(item.question),
          description: item.description ? t(item.description) : '',
          tooltip: item.tooltip ? t(item.tooltip) : '',
          extraProps: { ...item.extraProps, questionKey: item.question },
        })),
      );
      setQuestionFlow({ questionList: newQuestions, currentIndex: 0 });
      setPendingClose(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  // After data refresh settles, check for newly enabled questions or close
  useEffect(() => {
    if (!pendingClose || shouldRefreshMinorData) return;

    const currentCodes = new Set(questionFlow.questionList.map(q => q.code));
    const newExtras = extraQuestions
      .filter(item => !currentCodes.has(item.code))
      .map(item => ({
        ...item,
        question: t(item.question),
        description: item.description ? t(item.description) : '',
        tooltip: item.tooltip ? t(item.tooltip) : '',
        extraProps: { ...item.extraProps, questionKey: item.question },
      }));

    if (newExtras.length > 0) {
      setQuestionFlow(prev => appendExtraQuestions(prev, newExtras));
    } else {
      props.onClose();
    }
    setPendingClose(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingClose, shouldRefreshMinorData, extraQuestions]);

  const onNext = useCallback(() => {
    if (questionFlow.currentIndex >= questionFlow.questionList.length - 1) {
      dispatch(notifyMinorDataChanged());
      setPendingClose(true);
      return;
    }

    setQuestionFlow(prev => ({
      ...prev,
      currentIndex: prev.currentIndex + 1,
    }));
  }, [dispatch, questionFlow.currentIndex, questionFlow.questionList.length]);

  const renderQuestion = useCallback(() => {
    const currentQuestion =
      questionFlow.questionList[questionFlow.currentIndex];

    if (currentQuestion) {
      const commonProps: QuestionProps = {
        index: questionFlow.currentIndex,
        total: questionFlow.questionList.length,
        question: currentQuestion,
        onAnswered: onNext,
        pepAnswerTarget: 'minor',
      };

      switch (currentQuestion.code) {
        case QuestionCode.Relatives:
          return <QuestionAboutRelatives {...commonProps} />;
        case QuestionCode.USIndica:
          return <QuestionAboutUSIndicia {...commonProps} />;
        case QuestionCode.DualCitizenship:
          return <QuestionAboutDualCitizenship {...commonProps} />;
        case QuestionCode.RenouncedUSCitizenship:
          return <QuestionRenouncedUS {...commonProps} />;
        case QuestionCode.AccountTypeOrder:
          return <QuestionAccountTypeOrder {...commonProps} />;
        case QuestionCode.EXCPrimaryAccount:
          return <QuestionEXCPrimaryAccount {...commonProps} />;
        case QuestionCode.MinorConsentSign:
          return <MinorQuestionConsentSign {...commonProps} />;
        case QuestionCode.MinorOther:
          return <MinorQuestionOther {...commonProps} />;
      }
    }

    return null;
  }, [questionFlow.questionList, questionFlow.currentIndex, onNext]);

  return (
    <Drawer
      {...props}
      headerBgColor={(theme: Theme) => theme.palette.background.paper}
      title={<Typography variant="h5">{t('questionDrawer.title')}</Typography>}
    >
      <QuestionsContainer>{renderQuestion()}</QuestionsContainer>
    </Drawer>
  );
}

export function appendExtraQuestions(
  prev: { questionList: QuestionWithAnswer[]; currentIndex: number },
  newExtras: QuestionWithAnswer[],
) {
  const isPreExist = prev.questionList.some(q => q.code === newExtras[0].code);
  const questionsToAdd = uniqBy([...prev.questionList, ...newExtras], 'code');
  return {
    questionList: questionsToAdd,
    currentIndex: isPreExist ? prev.currentIndex : prev.currentIndex + 1,
  };
}
